import {
  CONFIG_GLOBAL,
  configFromScriptElement,
  normalizeConfig,
  resolveConfigFromDocument,
  type CustomerClientConfig,
} from './config.js';
import { createTamoomehClient, type TamoomehClient } from './client.js';
import {
  defineServiceRequestElement,
  SERVICE_REQUEST_TAG,
  ServiceRequestElement,
  setSharedClient,
} from './element.js';

/**
 * Browser entry — the artifact Tamoomeh and Porabzar actually load.
 *
 *   <script src="/assets/tamoomeh/tamoomeh-customer.js"
 *           data-api-base="https://api.tamoomeh.ir"
 *           data-auth-mode="same-site"
 *           defer></script>
 *   <tamoomeh-service-request service="vakil"></tamoomeh-service-request>
 *
 * It does three things and nothing else:
 *   1. reads its configuration from its own `<script>` tag (or from a global a
 *      host page set first),
 *   2. registers the custom element,
 *   3. exposes `window.TamoomehCustomer` for host pages that want to drive the
 *      client directly — the same escape hatch Porabzar's other assets offer
 *      through their `PorAbzar*` globals.
 *
 * Configuration is read *synchronously* at module evaluation, because
 * `document.currentScript` is only correct then. A host page that defers the
 * script and configures it from a `DOMContentLoaded` handler would race; reading
 * it here removes that class of bug entirely.
 */

const scriptElement =
  typeof document === 'undefined' ? null : (document.currentScript as HTMLScriptElement | null);

const globalConfig =
  typeof globalThis === 'undefined'
    ? undefined
    : ((globalThis as Record<string, unknown>)[CONFIG_GLOBAL] as
        | Partial<CustomerClientConfig>
        | undefined);

function bootConfig(): CustomerClientConfig | null {
  const fromScript = configFromScriptElement(scriptElement, globalConfig ?? {});
  if (fromScript) return fromScript;
  if (globalConfig?.apiBaseUrl) return globalConfig as CustomerClientConfig;
  return resolveConfigFromDocument() ?? null;
}

let client: TamoomehClient | null = null;
const rawConfig = bootConfig();
if (rawConfig) {
  try {
    client = createTamoomehClient(normalizeConfig(rawConfig));
    setSharedClient(client);
  } catch (error) {
    // A misconfigured embed must not break the host page's other scripts. The
    // element renders an explanatory state instead.
    if (typeof console !== 'undefined') {
      console.warn('[tamoomeh] customer client configuration rejected:', error);
    }
  }
}

defineServiceRequestElement();

/** Public surface for a host page. Mirrors the `PorAbzar*` global convention. */
const api = {
  tag: SERVICE_REQUEST_TAG,
  ServiceRequestElement,
  createTamoomehClient,
  defineServiceRequestElement,
  /** The page-wide client, or null when the script was not configured. */
  get client(): TamoomehClient | null {
    return client;
  },
  /** Replace the page-wide client (e.g. after a late configuration decision). */
  configure(config: CustomerClientConfig): TamoomehClient {
    client = createTamoomehClient(config);
    setSharedClient(client);
    return client;
  },
};

if (typeof globalThis !== 'undefined') {
  (globalThis as Record<string, unknown>)['TamoomehCustomer'] = api;
}

export * from './index.js';
export { api as TamoomehCustomer };
